import React from "react";
import { Box, Typography, styled } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { tokens } from "../../theme/tokens";
import { LoadingSkeleton } from "./helpers/StatePrimitives";

export const TableContainer = styled(Box)({
  backgroundColor: tokens.color.surface.default,
  border: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  borderRadius: tokens.radius.md,
  boxShadow: "none",
  height: "80vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
});

export const ScrollWrapper = styled(Box)({
  flexGrow: 1,
  overflowY: "auto",
  overflowX: "auto",
  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: tokens.color.border.strong,
    borderRadius: tokens.radius.full,
  },
});

export const TableHeader = ({ title }: { title: string }) => (
  <Box
    sx={{
      p: 2,
      flexShrink: 0,
      backgroundColor: tokens.color.surface.default,
    }}
  >
    <Typography
      variant="h6"
      sx={{
        fontWeight: tokens.type.denseStrong.weight,
        color: tokens.color.text.primary,
      }}
    >
      {title}
    </Typography>
  </Box>
);

export const StyledTable = styled("table")({
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  textAlign: "left",
  padding: tokens.space[3],
});

export const THead = styled("thead")({
  position: "sticky",
  top: 0,
  zIndex: 10,
  backgroundColor: tokens.color.surface.sunken,
});

export const Th = styled("th")<{ width?: string | number }>(({ width }) => ({
  padding: `${tokens.space[2]} ${tokens.space[3]}`,
  fontSize: tokens.type.denseStrong.size,
  fontWeight: tokens.type.denseStrong.weight,
  color: tokens.color.text.secondary,
  ...(width && { width }),
  backgroundColor: tokens.color.surface.sunken,
  borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  borderTop: `${tokens.borderWidth} solid ${tokens.color.border.default}`,

  "&:first-child": {
    borderLeft: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: `${tokens.radius.md} 0 0 0`,
  },
  "&:last-child": {
    borderRight: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
    borderRadius: `0 ${tokens.radius.md} 0 0`,
  },
  letterSpacing: "0.05em",
  whiteSpace: "nowrap",
}));

export const TBody = styled("tbody")({
  borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
});

export const Td = styled("td")<{
  align?: "left" | "center" | "right";
  width?: string | number;
}>(({ align = "left", width }) => ({
  padding: `${tokens.space[2]} ${tokens.space[3]}`,
  fontSize: tokens.type.dense.size,
  color: tokens.color.text.primary,
  borderBottom: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  textAlign: align,
  ...(width && {
    width,
    maxWidth: width,
    overflow: "hidden",
    overflowWrap: "break-word",
  }),
  "&:first-of-type": {
    borderLeft: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },
  "&:last-of-type": {
    borderRight: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  },

  "tr:last-child &": {
    "&:first-of-type": {
      borderBottomLeftRadius: tokens.radius.md,
    },
    "&:last-of-type": {
      borderBottomRightRadius: tokens.radius.md,
    },
  },
}));

export const Tr = styled("tr")({
  borderLeft: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  borderRight: `${tokens.borderWidth} solid ${tokens.color.border.default}`,
  "&:hover": {
    backgroundColor: tokens.color.surface.sunken,
  },
});

export const TableSkeleton = ({
  columns,
  mainColumns,
  rows = 10,
}: {
  columns: number;
  mainColumns: { title: string; width: number }[];
  rows?: number;
}) => {
  return (
    <>
      {[...Array(rows)].map((_, rowIndex) => (
        <Tr key={rowIndex}>
          {[...Array(columns)].map((_, colIndex) => (
            <Td key={colIndex} width={mainColumns && mainColumns[colIndex].width}>
              <LoadingSkeleton variant="text" width="80%" height={24} />
            </Td>
          ))}
        </Tr>
      ))}
    </>
  );
};

export type SortDirection = "asc" | "desc" | null;

interface SortableThProps {
  width?: string | number;
  sortable?: boolean;
  sortDirection?: SortDirection;
  onSort?: () => void;
  children: React.ReactNode;
}

export const SortableTh: React.FC<SortableThProps> = ({
  width,
  sortable = false,
  sortDirection = null,
  onSort,
  children,
}) => {
  if (!sortable) {
    return <Th width={width}>{children}</Th>;
  }

  return (
    <Th
      width={width}
      style={{ cursor: "pointer", userSelect: "none" }}
      onClick={onSort}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: tokens.space[1],
        }}
      >
        {children}
        {sortDirection === "asc" && (
          <ArrowUpwardIcon sx={{ fontSize: 14, color: tokens.color.text.primary }} />
        )}
        {sortDirection === "desc" && (
          <ArrowDownwardIcon sx={{ fontSize: 14, color: tokens.color.text.primary }} />
        )}
        {sortDirection === null && (
          <ArrowUpwardIcon sx={{ fontSize: 14, color: tokens.color.text.disabled }} />
        )}
      </Box>
    </Th>
  );
};
